import React, { useState } from 'react';

import type { PopoverProps } from '@mui/material';
import { difference } from 'ramda';
import type { ExistingContractType, ReportGridRow } from 'src/components';
import { ContractTypeChip } from 'src/components';
import { ContractTypePopper } from 'src/components/generic/ContractTypePopper';
import { createPendingContractType } from 'src/components/generic/pending-contract-type';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import type { StorableWithRoleInfo } from 'src/helpers';
import { useGetIsAuthorizedToEditStorable, useUpdateDocumentType } from 'src/hooks';

export interface ContractTypeDocument
  extends
    StorableWithRoleInfo,
    Pick<
      Apollo.Document,
      | 'id'
      | 'coreAiPropertiesExtractionStartedAt'
      | 'coreAiPropertiesExtractionFinishedAt'
      | 'coreAiPropertiesExtractionFailedAt'
      | 'variant'
    > {
  latestVersion: ReportGridRow['latestVersion'];
  type: Pick<Apollo.ContractType, 'id' | '__typename' | 'value'> | null;
}

interface ContractTypePickerProps {
  document: ContractTypeDocument;
  popoverProps?: Partial<Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>>;
}

export function ContractTypePicker({
  document,
  popoverProps,
}: ContractTypePickerProps): JSX.Element {
  const { anchorEl, disabledReason, handleChangeType, openPopper, setAnchorEl, setOpenPopper } =
    useContractTypePicker({ document });

  return (
    <>
      <ContractTypeChip
        ref={setAnchorEl}
        contractType={document.type}
        onClick={e => {
          e.stopPropagation();
          setOpenPopper(true);
        }}
        onDelete={() => handleChangeType(null)}
      />
      <ContractTypePopper
        suggestNewOption
        anchorEl={anchorEl}
        disabledReason={disabledReason}
        open={openPopper}
        popoverProps={popoverProps}
        value={document.type ? [document.type] : []}
        onChange={(_e, contractTypes) => {
          setOpenPopper(false);

          // Picking the current type again keeps it; the chip's delete icon removes it.
          const addedType = difference(contractTypes, document.type ? [document.type] : []).at(0);
          if (!addedType) return;
          if (addedType.__typename === 'NewContractType')
            return handleChangeType(createPendingContractType(addedType.value));

          return handleChangeType(addedType);
        }}
        onClose={() => setOpenPopper(false)}
      />
    </>
  );
}

export function useContractTypePicker({ document }: ContractTypePickerProps) {
  const currentUser = useSelectCurrentUser();
  const { updateDocumentType } = useUpdateDocumentType();
  const { getIsAuthorizedToEditStorable } = useGetIsAuthorizedToEditStorable();
  const { canEdit } = getIsAuthorizedToEditStorable(document);
  const [openPopper, setOpenPopper] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const disabledReason =
    currentUser?.role !== Apollo.OrgRole.Admin
      ? 'Only admins can create contract types'
      : !canEdit
        ? 'Only editors and the document owner can edit contract type'
        : '';

  /**
   * The chip updates optimistically and rolls back if the request fails; the
   * Apollo error link already shows the failure, so only log it here.
   */
  async function handleChangeType(value: ExistingContractType | null): Promise<void> {
    try {
      await updateDocumentType({ contractType: value, documentId: document.id });
    } catch (error) {
      console.error('Error updating contract type:', error);
    }
  }

  return {
    anchorEl,
    disabledReason,
    handleChangeType,
    openPopper,
    setAnchorEl,
    setOpenPopper,
  };
}
